import { Link } from 'react-router-dom'
import SEO, { breadcrumbs } from '../components/common/SEO'
import ResponsiveImage from '../components/common/ResponsiveImage'
import { Breadcrumbs, BookButton, BulletList, SideCard, BookingBand } from '../components/detail/DetailBlocks'
import { teamBySlug } from '../data/team'
import { servicePages } from '../data/servicePages'
import { SITE_URL } from '../data/site'
import NotFound from './NotFound'

const MAX_DESCRIPTION = 155

const buildDescription = (member) => {
  const base = `${member.name}, ${member.fullTitle} at Recreo Rehab, Yonge & Eglinton.`
  let focus = ''
  for (const s of member.specialties || []) {
    const next = focus ? `${focus}; ${s}` : ` Focus: ${s}`
    if ((base + next + '.').length > MAX_DESCRIPTION) break
    focus = next
  }
  return `${base}${focus}${focus ? '.' : ''}`
}

const PractitionerPage = ({ slug }) => {
  const member = teamBySlug(slug)
  if (!member) return <NotFound />

  const path = `/team/${member.slug}`
  const trail = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: member.name, path },
  ]
  const services = servicePages.filter((s) => s.practitioners.includes(member.slug))
  const firstName = member.name.split(' ')[0]

  const metaDescription = buildDescription(member)

  return (
    <>
      <SEO
        title={`${member.name}, ${member.title} — Midtown Toronto`}
        description={metaDescription}
        path={path}
        image={member.image}
        imageAlt={`${member.name}, ${member.fullTitle}`}
        type="profile"
        structuredData={[
          breadcrumbs(trail),
          {
            '@context': 'https://schema.org',
            '@type': 'ProfilePage',
            url: `${SITE_URL}${path}`,
            mainEntity: {
              '@type': 'Person',
              '@id': `${SITE_URL}${path}#person`,
              name: member.name,
              honorificSuffix: member.title,
              jobTitle: member.fullTitle,
              description: member.shortBio,
              image: `${SITE_URL}${member.image}`,
              url: `${SITE_URL}${path}`,
              worksFor: { '@id': `${SITE_URL}/#business` },
              knowsAbout: member.specialties,
              hasCredential: member.certifications.map((credential) => ({
                '@type': 'EducationalOccupationalCredential',
                name: credential,
              })),
              ...(member.registration
                ? { identifier: { '@type': 'PropertyValue', name: 'Registration', value: member.registration } }
                : {}),
            },
          },
        ]}
      />

      <div className="bg-cream-200 pt-24 md:pt-28">
        <Breadcrumbs items={trail} />
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="grid md:grid-cols-[320px_1fr] gap-10 md:gap-16 items-start">
            <div className="md:sticky md:top-28">
              <div className="rounded-[20px] overflow-hidden shadow-lg">
                <ResponsiveImage
                  src={member.image}
                  alt={`${member.name}, ${member.fullTitle}`}
                  sizes="(min-width: 768px) 320px, 100vw"
                  loading="eager"
                  className="w-full aspect-[4/5] object-cover object-[center_18%]"
                />
              </div>
              <BookButton label={`Book with ${firstName}`} className="mt-6 w-full text-center" />
            </div>

            <div>
              <p className="font-sans text-dark-400 text-xs tracking-[0.2em] uppercase mb-3">
                {member.fullTitle}
              </p>
              <h1 className="font-display text-dark-800 text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1] mb-8">
                {member.name}, {member.title}
              </h1>

              <div className="space-y-4 mb-10">
                {member.bio.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="font-sans text-dark-500 text-base md:text-lg leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                {member.specialties?.length > 0 && (
                  <SideCard>
                    <h2 className="font-display text-dark-700 text-lg font-semibold mb-3">Specialties</h2>
                    <BulletList items={member.specialties} />
                  </SideCard>
                )}
                {member.certifications?.length > 0 && (
                  <SideCard>
                    <h2 className="font-display text-dark-700 text-lg font-semibold mb-3">
                      Certifications &amp; Training
                    </h2>
                    <BulletList items={member.certifications} dot="bg-brand-green-600" />
                    {member.registration && (
                      <p className="font-sans text-dark-400 text-sm mt-4">
                        Registration #{member.registration}
                      </p>
                    )}
                  </SideCard>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {member.treatmentStyle && (
                  <SideCard>
                    <h2 className="font-display text-dark-700 text-lg font-semibold mb-3">Treatment Style</h2>
                    {member.treatmentStyle.pressure && (
                      <p className="font-sans text-dark-500 text-base leading-relaxed">
                        <span className="text-dark-700 font-medium">Pressure:</span>{' '}
                        {member.treatmentStyle.pressure}
                      </p>
                    )}
                    {member.treatmentStyle.approach && (
                      <p className="font-sans text-dark-500 text-base leading-relaxed">
                        <span className="text-dark-700 font-medium">Approach:</span>{' '}
                        {member.treatmentStyle.approach}
                      </p>
                    )}
                  </SideCard>
                )}
                {services.length > 0 && (
                  <SideCard>
                    <h2 className="font-display text-dark-700 text-lg font-semibold mb-3">
                      Book {firstName} for
                    </h2>
                    <ul className="space-y-2">
                      {services.map((s) => (
                        <li key={s.slug}>
                          <Link
                            to={`/services/${s.slug}`}
                            className="font-sans text-base text-brand-orange-600 hover:text-brand-orange-500 underline underline-offset-4"
                          >
                            {s.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </SideCard>
                )}
              </div>

              {member.outsideClinic && (
                <div className="mt-10">
                  <h2 className="font-display text-dark-700 text-xl font-semibold mb-3">Outside the Clinic</h2>
                  <p className="font-sans text-dark-500 text-base leading-relaxed">{member.outsideClinic}</p>
                </div>
              )}

              <Link
                to="/about"
                className="inline-block mt-10 font-sans text-xs tracking-[0.15em] uppercase text-dark-600 hover:text-brand-orange-500"
              >
                ← Meet the rest of the team
              </Link>
            </div>
          </div>
        </section>
      </div>

      <BookingBand label={`Book with ${firstName}`} />
    </>
  )
}

export default PractitionerPage
